import { AppleLogoIcon } from "@/components/Icons/apple-logo-icon";
import { Button } from "@/components/ui/button";
import { webEnv } from "@/config/webEnv";
import { useHandleError } from "@/hooks/useHandleError";
import { useLoginWithApple } from "@roman/shared/hooks/auth/useLoginWithApple";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type { AppleLoginCallbackArgs } from "@roman/shared/types/Apple";
import { getPlatform } from "@roman/shared/utils/platform";
import { type AppleAuthResponse, appleAuthHelpers, useScript } from "react-apple-signin-auth";
import { toast } from "sonner";

type OnClickInput = {
  onSuccess: (response: AppleAuthResponse) => Promise<void>;
  onError: (input: { error: string }) => void;
};

const clientId = webEnv.APPLE_CLIENT_ID;

const redirectURI = webEnv.APPLE_REDIRECT_URI;

export const AppleLoginButton = ({ text }: { text: string }) => {
  const platform = getPlatform();
  const isPC = platform === "pc";
  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);
  const { trigger: triggerLoginWithApple } = useLoginWithApple(useHandleError);
  const loginWithApple = useAuthStore((state) => state.loginWithApple);

  const onSuccess = async (response: AppleAuthResponse) => {
    const code = response.authorization.code;

    const trigger = async ({ onSuccess, onError }: AppleLoginCallbackArgs) => {
      await triggerLoginWithApple(
        { code },
        {
          onSuccess,
          onError,
        },
      );
    };

    await loginWithApple(trigger, toast);
  };

  const onError = ({ error }: { error: string }) => {
    if (error === "popup_closed_by_user") return;
    toast.error("Appleでのログインに失敗しました");
  };

  const appleLogin =
    ({ onSuccess, onError }: OnClickInput) =>
    () => {
      appleAuthHelpers.signIn({
        authOptions: {
          clientId,
          redirectURI,
          scope: "email name",
          usePopup: true,
        },
        onSuccess,
        onError,
      });
    };

  return (
    <Button
      variant="outline"
      onClick={appleLogin({ onSuccess, onError })}
      className={`flex items-center w-[350px] px-4 py-6 rounded-full border-black bg-black transition-all ease-out active:scale-95 active:shadow-sm ${isPC ? "hover:scale-105 hover:shadow-md hover:bg-black" : ""}`}
    >
      <AppleLogoIcon />
      <span className="font-bold flex-grow text-center text-base text-white">{text}</span>
      <div className="w-5" />
    </Button>
  );
};
